import { useEffect, useState } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { CheckCircle2, Loader2, XCircle, Mail } from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

export default function EventSuccess() {
  const { eventId } = useParams();
  const [searchParams] = useSearchParams();
  const sessionId = searchParams.get('session_id');
  const bookingId = searchParams.get('booking_id');
  const isFree = searchParams.get('free') === 'true';

  const [status, setStatus] = useState('loading'); // loading | confirmed | error
  const [eventName, setEventName] = useState('');
  const [errorMsg, setErrorMsg] = useState('');

  useEffect(() => {
    const confirm = async () => {
      if (isFree) {
        // Free events are already confirmed by the book endpoint
        try {
          const ev = await axios.get(`${API}/events/${eventId}`);
          setEventName(ev.data.name);
        } catch (_) {}
        setStatus('confirmed');
        return;
      }
      if (!sessionId || !bookingId) {
        setStatus('error');
        setErrorMsg('Missing payment information. Please contact the venue.');
        return;
      }
      try {
        const res = await axios.post(
          `${API}/events/${eventId}/confirm?session_id=${sessionId}&booking_id=${bookingId}`
        );
        if (res.data.status === 'confirmed' || res.data.status === 'already_confirmed') {
          try {
            const ev = await axios.get(`${API}/events/${eventId}`);
            setEventName(ev.data.name);
          } catch (_) {}
          setStatus('confirmed');
        } else {
          setStatus('error');
          setErrorMsg('Payment could not be confirmed. Please contact the venue.');
        }
      } catch (err) {
        setStatus('error');
        setErrorMsg(err.response?.data?.detail || 'Could not confirm payment. Please contact the venue.');
      }
    };
    confirm();
  }, [eventId, sessionId, bookingId, isFree]);

  return (
    <div className="min-h-screen bg-slate-950 text-white flex items-center justify-center px-4">
      <div className="max-w-md w-full text-center">

        {status === 'loading' && (
          <>
            <Loader2 className="h-14 w-14 text-slate-400 mx-auto mb-5 animate-spin" />
            <h1 className="text-2xl font-bold mb-2">Confirming your booking…</h1>
            <p className="text-slate-400">Please wait, this only takes a moment.</p>
          </>
        )}

        {status === 'confirmed' && (
          <>
            <div className="w-20 h-20 rounded-full bg-emerald-500/15 flex items-center justify-center mx-auto mb-5">
              <CheckCircle2 className="h-10 w-10 text-emerald-400" />
            </div>
            <h1 className="text-3xl font-extrabold mb-2">You're on the list!</h1>
            {eventName && (
              <p className="text-slate-400 text-lg mb-5">{eventName}</p>
            )}
            <div className="bg-slate-900 border border-slate-800 rounded-2xl p-6 text-left space-y-3">
              <div className="flex items-start gap-3">
                <Mail className="h-5 w-5 text-emerald-400 flex-shrink-0 mt-0.5" />
                <div>
                  <p className="font-semibold text-white text-sm">Check your email</p>
                  <p className="text-slate-400 text-sm">
                    Your QR code ticket{isFree ? '' : 's'} have been sent to the email address you provided.
                    Show them at the door to gain entry.
                  </p>
                </div>
              </div>
            </div>
            <p className="text-slate-600 text-xs mt-6">
              Didn't receive an email? Check your spam folder or contact the venue.
            </p>
          </>
        )}

        {status === 'error' && (
          <>
            <div className="w-20 h-20 rounded-full bg-red-500/15 flex items-center justify-center mx-auto mb-5">
              <XCircle className="h-10 w-10 text-red-400" />
            </div>
            <h1 className="text-2xl font-bold mb-2">Something went wrong</h1>
            <p className="text-slate-400 mb-4">{errorMsg}</p>
            <a href={`/event/${eventId}`} className="text-sm text-slate-400 underline underline-offset-4 hover:text-white">
              ← Back to event page
            </a>
          </>
        )}

      </div>
    </div>
  );
}
